import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentKindBadge } from '@/features/invoices/DocumentKindBadge';
import { formatDate, formatMoney } from '@/features/invoices/format';
import { StatusBadge } from './StatusBadge';
import type { OutboundInvoice } from './api';

/** Fraction of the loaded list scrolled past before the next page is prefetched (mirrors InvoiceTable). */
const PREFETCH_AT = 0.85;
const prefetchIndex = (count: number) => Math.floor(count * PREFETCH_AT);

/**
 * Load-more wiring driven by the infinite-scroll sentinel. All optional so the components render fine
 * without pagination (e.g. in isolation tests) — the sentinel is simply not shown.
 */
interface LoadMore {
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

interface OutboundListProps extends LoadMore {
  items: OutboundInvoice[];
  lng: string;
  /** Open the invoice's status/timeline page. */
  onOpen: (id: string) => void;
}

/** The counterparty (buyer), with the NIP as a muted sub-line when present. */
function Party({ invoice }: { invoice: OutboundInvoice }) {
  return (
    <div className="leading-tight">
      <div>{invoice.buyerName}</div>
      {invoice.buyerNip ? (
        <div className="text-xs text-muted-foreground">{invoice.buyerNip}</div>
      ) : null}
    </div>
  );
}

interface RowProps<E extends HTMLElement> {
  invoice: OutboundInvoice;
  lng: string;
  onOpen: (id: string) => void;
  /** Callback ref that arms the next-page prefetch; set only on the trigger row (see prefetchIndex). */
  triggerRef?: (node: E | null) => void;
}

// Memoized so appending a page only renders the new rows (see InvoiceTable for the rationale).
const OutboundTableRow = memo(function OutboundTableRow({
  invoice,
  lng,
  onOpen,
  triggerRef,
}: RowProps<HTMLTableRowElement>) {
  return (
    <TableRow ref={triggerRef} className="cursor-pointer" onClick={() => onOpen(invoice.id)}>
      <TableCell className="text-muted-foreground">{formatDate(lng, invoice.issueDate)}</TableCell>
      <TableCell className="font-medium">
        <div className="leading-tight">
          <div>{invoice.invoiceNumber}</div>
          {invoice.ksefNumber ? (
            <div className="text-xs text-muted-foreground">{invoice.ksefNumber}</div>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <Party invoice={invoice} />
      </TableCell>
      <TableCell>
        <DocumentKindBadge kind={invoice.documentKind} taxScheme={invoice.taxScheme} />
      </TableCell>
      <TableCell>
        <StatusBadge state={invoice.state} />
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {formatMoney(lng, Number(invoice.netTotal), invoice.currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {formatMoney(lng, Number(invoice.vatTotal), invoice.currency)}
      </TableCell>
      <TableCell className="text-right font-medium tabular-nums">
        {formatMoney(lng, Number(invoice.grossTotal), invoice.currency)}
      </TableCell>
    </TableRow>
  );
});

export function OutboundTable({
  items,
  lng,
  onOpen,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}: OutboundListProps) {
  const { t } = useTranslation();
  const loadMoreRef = useInfiniteScroll<HTMLTableRowElement>(onLoadMore, hasMore && !isLoadingMore);
  const triggerIndex = prefetchIndex(items.length);
  return (
    // Fill the height the list page hands down and scroll inside the ScrollArea; the header sticks to
    // the top of that scroll viewport so column labels stay visible.
    <Table containerClassName="min-h-0 flex-1">
      <TableHeader className="sticky top-0 z-10 bg-card">
        <TableRow>
          <TableHead>{t('invoices.columns.issued')}</TableHead>
          <TableHead>{t('invoices.columns.number')}</TableHead>
          <TableHead>{t('invoices.columns.party')}</TableHead>
          <TableHead>{t('invoices.columns.type')}</TableHead>
          <TableHead>{t('invoices.columns.status')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.net')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.vat')}</TableHead>
          <TableHead className="text-right">{t('invoices.columns.amount')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((invoice, i) => (
          <OutboundTableRow
            key={invoice.id}
            invoice={invoice}
            lng={lng}
            onOpen={onOpen}
            triggerRef={i === triggerIndex ? loadMoreRef : undefined}
          />
        ))}
        {isLoadingMore ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={8} className="py-4 text-center text-sm text-muted-foreground">
              {t('invoices.outbound.loading_more')}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

// Memoized for the same reason as OutboundTableRow — only newly appended rows render on a page load.
const OutboundCardRow = memo(function OutboundCardRow({
  invoice,
  lng,
}: {
  invoice: OutboundInvoice;
  lng: string;
}) {
  return (
    <Link
      to={`/invoices/sent/${invoice.id}`}
      className="flex items-center gap-3 px-4 py-3 outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{invoice.invoiceNumber}</span>
          <StatusBadge state={invoice.state} />
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {invoice.buyerName} · {formatMoney(lng, Number(invoice.grossTotal), invoice.currency)} ·{' '}
          {formatDate(lng, invoice.issueDate)}
        </div>
      </div>
    </Link>
  );
});

/** Small-screen alternative to the table: one compact row per invoice. */
export function OutboundCardList({
  items,
  lng,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}: Omit<OutboundListProps, 'onOpen'>) {
  const { t } = useTranslation();
  const loadMoreRef = useInfiniteScroll<HTMLLIElement>(onLoadMore, hasMore && !isLoadingMore);
  const triggerIndex = prefetchIndex(items.length);
  return (
    <ul className="divide-y">
      {items.map((invoice, i) => (
        <li key={invoice.id} ref={i === triggerIndex ? loadMoreRef : undefined}>
          <OutboundCardRow invoice={invoice} lng={lng} />
        </li>
      ))}
      {isLoadingMore ? (
        <li className="py-3 text-center text-sm text-muted-foreground">
          {t('invoices.outbound.loading_more')}
        </li>
      ) : null}
    </ul>
  );
}
